import { useCallback, useEffect, useMemo, useRef } from "react";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  creativeWithResolvedInstagramRowOnly,
  facebookPageWithCatalogLinkedMetadata,
  instagramSelectOptionsFromAccounts,
  resolveAdvertiserInstagramOnFacebookPagePicked,
} from "../utils/creativesWithInstagramFromFetch";

type UseAdvertiserInstagramPageSyncArgs = {
  creative: CreativeBlock;
  catalogPages: FacebookPage[];
  catalogAccounts: InstagramAccount[];
  /** Guided create only: auto-select page-linked Instagram when the user picks a page. */
  applyDefaultOnFacebookPageChange?: boolean;
  instagramClearDisabled?: boolean;
  onPatch: (patch: Partial<CreativeBlock>) => void;
};

/**
 * Advertiser identity pickers backed by the preloaded ad-account catalogs.
 * Edit flows keep Facebook-only creatives unless the user explicitly picks Instagram.
 */
export function useAdvertiserInstagramPageSync({
  creative,
  catalogPages,
  catalogAccounts,
  applyDefaultOnFacebookPageChange = false,
  instagramClearDisabled = false,
  onPatch,
}: UseAdvertiserInstagramPageSyncArgs) {
  const creativeRef = useRef(creative);
  creativeRef.current = creative;

  const mergedInstagramOptions = useMemo(
    () =>
      instagramSelectOptionsFromAccounts(
        catalogAccounts,
        creative.instagramAccount
      ),
    [catalogAccounts, creative.instagramAccount]
  );

  useEffect(() => {
    const current = creativeRef.current;
    const selectedId = current.instagramAccount?.id?.trim();
    if (!selectedId) {
      return;
    }
    const canonical = creativeWithResolvedInstagramRowOnly(
      current,
      catalogAccounts
    );
    if (
      canonical.instagramAccount?.id === current.instagramAccount?.id &&
      canonical.instagramAccount?.username ===
        current.instagramAccount?.username &&
      canonical.instagramAccount?.name === current.instagramAccount?.name
    ) {
      return;
    }
    onPatch({ instagramAccount: canonical.instagramAccount });
  }, [
    catalogAccounts,
    creative.instagramAccount?.id,
    creative.instagramAccount?.username,
    creative.instagramAccount?.name,
    onPatch,
  ]);

  const handleFacebookPageChange = useCallback(
    (selectedPage: FacebookPage | null) => {
      if (!selectedPage) {
        onPatch({
          fbPage: null,
          ...(instagramClearDisabled ? {} : { instagramAccount: null }),
        });
        return;
      }

      const { page, instagramAccount } =
        resolveAdvertiserInstagramOnFacebookPagePicked({
          page: selectedPage,
          catalogPages,
          catalogAccounts,
          instagramClearDisabled,
          autoSelectLinkedInstagram: applyDefaultOnFacebookPageChange,
        });
      onPatch({
        fbPage: page,
        ...(instagramClearDisabled ? {} : { instagramAccount }),
      });
    },
    [
      applyDefaultOnFacebookPageChange,
      catalogAccounts,
      catalogPages,
      instagramClearDisabled,
      onPatch,
    ]
  );

  const handleInstagramChange = useCallback(
    (instagramAccount: InstagramAccount | null) => {
      if (instagramClearDisabled && !instagramAccount) {
        return;
      }
      onPatch({ instagramAccount });
    },
    [instagramClearDisabled, onPatch]
  );

  return {
    mergedInstagramOptions,
    handleFacebookPageChange,
    handleInstagramChange,
  };
}
